import { RequestStatus } from "@fitness-app/data-models";
import { TraineeActivityEventType } from "@fitness-app/data-models/businessEvents/domain/traineeActivity/TraineeActivityEvents";
import { type ClientActivityEvent } from "@fitness-app/data-models/entities/ClientActivity";
import { type ClientSurvey } from "@fitness-app/data-models/entities/ClientSurvey";
import { type Trainee } from "@fitness-app/data-models/entities/Trainee";

import { type AppThunk } from "../../../index";
import { traineeMeasurementsActions } from "../../traineeMeasurements";
import { addSurvey, updateSurvey } from "../../traineeSurveys/reducer";
import { addActivityEvent, updateActivityEvent } from "../reducer";
import { fetchClientRating } from "./fetchClientRating";
import { fetchTrainee } from "./fetchTrainee";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

// One retry - a channel that fails to join twice in a row is not a hiccup, and
// realtime-js keeps its own socket-level reconnect underneath anyway.
const MAX_SUBSCRIBE_ATTEMPTS = 2;

// A failed join is not a verdict: phoenix marks the channel `errored` and rejoins it on its
// own timer (1s, 2s, 5s, then 10s apart), and every join gets 10s to answer - so two full
// rejoin cycles fit inside 30s. Waiting that long before judging is the whole point: on
// mobile practically every TIMED_OUT is a cellular hiccup the channel recovers from by
// itself, which is what MOBILE-APP-K2 had been reporting (1109 events / 338 users in a
// month, none of them a channel that stayed dead).
const RECOVERY_GRACE_MS = 30_000;

const attachTraineeUpdates =
  (traineeId: string, claimedSlot: number): AppThunk =>
  async (dispatch, getState, { db, subscriptions, errorTrackingService, isAppActive }) => {
    try {
      const channelName = `trainee-${traineeId}`;

      // Shared across retries: one pending verdict at a time, carrying whatever the channel
      // last complained about.
      let recoveryWatchdog: ReturnType<typeof setTimeout> | null = null;
      let lastFailure: { status: string; error?: Error } | null = null;

      const clearRecoveryWatchdog = () => {
        if (recoveryWatchdog) {
          clearTimeout(recoveryWatchdog);
          recoveryWatchdog = null;
        }
      };

      const openChannel = async (attempt: number) => {
        if (!ownsRealtimeSlot(subscriptions, "trainee", claimedSlot)) {
          return;
        }

        // Detaching the previous channel has to finish before a new one is created: until
        // it does, `db.channel()` hands back the old, still-joined channel and the first
        // `.on()` below throws (TRE-1759).
        const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeTraineeChannel);

        // that detach is another await, so the slot could have changed hands meanwhile
        if (!ownsRealtimeSlot(subscriptions, "trainee", claimedSlot)) {
          await db.removeChannel(channel);
          return;
        }

        subscriptions.traineeChannelName = channelName;
        subscriptions.realtimeTraineeChannel = channel;

        const retry = async () => {
          if (!ownsRealtimeSlot(subscriptions, "trainee", claimedSlot)) {
            return;
          }

          clearRecoveryWatchdog();
          await db.removeChannel(channel);

          // The same profile may have been reopened while channel removal was pending.
          if (!ownsRealtimeSlot(subscriptions, "trainee", claimedSlot)) {
            return;
          }

          errorTrackingService?.log(`[subscribeToTrainee]: retrying channel for trainee ${traineeId}`);
          await openChannel(attempt + 1);
        };

        channel
          .on<Trainee>(
            "postgres_changes",
            { event: "*", schema: "public", table: "trainee", filter: `id=eq.${traineeId}` },
            (payload) => {
              if (payload.eventType === "UPDATE") {
                void dispatch(fetchTrainee({ id: traineeId, refetch: true }));
              }
            },
          )
          .on<ClientActivityEvent>(
            "postgres_changes",
            { event: "*", schema: "public", table: "client_activity_event", filter: `traineeId=eq.${traineeId}` },
            (payload) => {
              if (payload.eventType === "INSERT") {
                if (payload.new.eventName !== TraineeActivityEventType.TraineeSentChatMessage) {
                  if (payload.new.eventName === TraineeActivityEventType.TraineeAddedWeightMeasurement) {
                    void dispatch(traineeMeasurementsActions.fetchLatestWeightMeasurement({ traineeId }));
                  }

                  dispatch(addActivityEvent(payload.new));
                }
              }

              if (payload.eventType === "UPDATE") {
                dispatch(updateActivityEvent(payload.new));
              }
            },
          )
          .on<ClientSurvey>(
            "postgres_changes",
            { event: "*", schema: "public", table: "client_survey", filter: `traineeId=eq.${traineeId}` },
            (payload) => {
              if (getState().traineeSurveys.surveysStatus === RequestStatus.SUCCESS) {
                if (payload.eventType === "INSERT") {
                  dispatch(addSurvey(payload.new));
                }

                if (payload.eventType === "UPDATE") {
                  dispatch(updateSurvey(payload.new));
                }
              }
            },
          )
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "client_rating_snapshot", filter: `traineeId=eq.${traineeId}` },
            () => {
              void dispatch(fetchClientRating({ traineeId }));
            },
          )
          .subscribe((status, error) => {
            // The trainee profile refreshes itself from this channel (archived/assigned
            // program, activity feed, surveys, rating), so a dead channel means the view
            // silently goes stale. Report it instead of failing quietly.
            const failed = Boolean(error) || status === "CHANNEL_ERROR" || status === "TIMED_OUT";

            if (!failed) {
              // SUBSCRIBED on entering the profile, CLOSED when leaving it - both expected.
              clearRecoveryWatchdog();
              errorTrackingService?.log(
                `[subscribeToTrainee]: channel for trainee ${traineeId} is ${status} (attempt ${attempt})`,
              );
              return;
            }

            // A channel the profile no longer uses - the trainer left it or opened
            // another client - is expected to end this way, most often after the tab
            // sat in the background. Nothing is stale, so it is not worth an error.
            if (!ownsRealtimeSlot(subscriptions, "trainee", claimedSlot)) {
              errorTrackingService?.log(
                `[subscribeToTrainee]: stale channel ${channelName} ended with ${status} (attempt ${attempt})`,
              );
              return;
            }

            // The whole socket went down - a sleeping laptop, a network switch, a closing
            // tab - and phoenix fans that out as CHANNEL_ERROR to every channel at once.
            // realtime-js reconnects and rejoins on its own, so tearing this channel down
            // and rebuilding it only fights that; and one drop would otherwise report as
            // many errors as the session has channels.
            if (!db.realtime.isConnected()) {
              errorTrackingService?.log(
                `[subscribeToTrainee]: socket is down, leaving the reconnect to realtime-js (${error?.message ?? status})`,
              );
              return;
            }

            // A backgrounded app keeps a socket the OS has already suspended: it still
            // reports as connected, but heartbeats stop landing and phoenix ends every
            // channel with TIMED_OUT (or a "heartbeat timeout" error). realtime-js rejoins
            // on the next foreground, so this is expected mobile behaviour rather than a
            // dead channel - MOBILE-APP-JH caught it with `in_foreground: false`.
            if (isAppActive && !isAppActive()) {
              errorTrackingService?.log(
                `[subscribeToTrainee]: app is backgrounded, leaving the rejoin to realtime-js (${error?.message ?? status})`,
              );
              return;
            }

            // Everything that reaches this point is a live channel on a live socket that
            // failed to join - and phoenix is already rejoining it. Reporting now would only
            // say "a join failed once", which is not the question; the question is whether
            // the profile ends up stale. So record the complaint and let the channel have
            // RECOVERY_GRACE_MS to answer it.
            lastFailure = { status, error };

            if (recoveryWatchdog) {
              return;
            }

            errorTrackingService?.log(
              `[subscribeToTrainee]: channel for trainee ${traineeId} ended with ${status}, waiting for the rejoin (attempt ${attempt})`,
            );

            recoveryWatchdog = setTimeout(() => {
              recoveryWatchdog = null;

              const failure = lastFailure;
              lastFailure = null;

              // The trainer left the profile or opened another one meanwhile - whatever this
              // channel is doing no longer shows anywhere.
              if (!ownsRealtimeSlot(subscriptions, "trainee", claimedSlot) || !failure) {
                return;
              }

              if (channel.state === "joined") {
                errorTrackingService?.log(
                  `[subscribeToTrainee]: channel for trainee ${traineeId} rejoined itself after ${failure.status}`,
                );
                return;
              }

              const canRetry = attempt < MAX_SUBSCRIBE_ATTEMPTS;

              errorTrackingService?.recordError(
                failure.error ?? new Error(`Trainee realtime channel ended with status ${failure.status}`),
                "trainee_subscribe_error",
                {
                  trainee_id: traineeId,
                  channel: channelName,
                  status: failure.status,
                  attempt,
                  will_retry: canRetry,
                  channel_state: channel.state,
                  error: failure.error?.message,
                },
              );

              if (canRetry) {
                void retry();
                return;
              }

              // Out of attempts on the channel the profile is actually showing. Live
              // updates are gone until the trainer navigates, so pull the profile once
              // more - better a fresh snapshot than a silently stale screen.
              void dispatch(fetchTrainee({ id: traineeId, refetch: true }));
            }, RECOVERY_GRACE_MS);
          });
      };

      await openChannel(1);
    } catch (error) {
      errorTrackingService?.recordError(error, "trainee_subscribe_error", {
        trainee_id: traineeId,
        channel: subscriptions.traineeChannelName,
      });
    }
  };

export const subscribeToTraineeUpdates =
  (traineeId: string): AppThunk =>
  (dispatch, _, { subscriptions }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "trainee");
    return dispatch(attachTraineeUpdates(traineeId, claimedSlot));
  };

// Existing trainer/web callers own both initial loading and realtime through this action.
// Mobile navigation already loads the profile and subscribes to updates separately.
export const subscribeToTrainee =
  (traineeId: string): AppThunk =>
  async (dispatch, getState, { subscriptions }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "trainee");
    const profile = getState().trainee.profile;
    await dispatch(fetchTrainee({ id: traineeId, refetch: profile?.id === traineeId }));
    await dispatch(attachTraineeUpdates(traineeId, claimedSlot));
  };
